import { ConnectionUtils } from "@inter-ikea-core/core";
import RemovedLeftSideUnitComponent from "../components/RemovedLeftSideUnitComponent";
import RemovedRightSideUnitComponent from "../components/RemovedRightSideUnitComponent";
import ReplaceEntityComponent from "../components/ReplaceEntityComponent";
import { LEFT } from "../constants";
import { COMPONENT_IDS } from "../storage2ComponentIDs";
import { getEntityProductRef, hasRemovedSideUnit } from "../utils/entityUtils";
import { getAttachmentComponentBySide, getEntityAttachmentComponents, getOppositeSide, getRemovedSideUnitComponentBasedOnSide, } from "./attachedTo";
import { calculateCrossbrace } from "./calculateCrossBrace";
import { selectChildEntityByPartKey, sideUnitKeys } from "./sideUnitHandler";
import { entitiesAreEqualDepth, entitiesAreEqualHeight, getShortestEntity, shouldReplaceSection, } from "./sideUnitReplacement";
export const handleSectionReplacement = (_entity, _productRef) => {
    const { left, right } = getEntityAttachmentComponents(_entity);
    if (!left || !right)
        throw new Error("Entity didn't have expected attachments");
    return _entity.addComponent(new ReplaceEntityComponent(_productRef, [left, right]));
};
export const handleCrossBrace = (_entity) => {
    const sectionComponent = _entity.getComponent(COMPONENT_IDS.SECTION_COMPONENT);
    if (!sectionComponent)
        throw new Error("No section component found.");
    sectionComponent.crossbraceCalculation && calculateCrossbrace(_entity);
};
/**
 * Handles attachment events
 * @param _entity              - Entity
 * @param _entityPlacementSide - Either left or right.
 *                               If the entity is standing to the left with a new attachment on
 *                               it's right side, `entityPlacementSide` should be left.
 *                               If the entity is standing to the right with a new attachment on
 *                               it's left side, `entityPlacementSide` should be right.
 */
export const handleAttachment = (_entity, _entityPlacementSide) => {
    if (!entitiesAreEqualDepth(_entity, _entityPlacementSide))
        return;
    const { shouldReplace, withProductRef } = shouldReplaceSection(_entity);
    handleSingleSideAttachment(_entity, _entityPlacementSide);
    if (shouldReplace)
        return handleSectionReplacement(_entity, withProductRef);
    handleCrossBrace(_entity);
};
/**
 * Handles detachment events
 * @param _entity
 * @param _entityPlacementSide - Either left or right.
 *                               If the entity is standing to the left with a new attachment on
 *                               it's right side, `entityPlacementSide` should be left.
 *                               If the entity is standing to the right with a new attachment on
 *                               it's left side, `entityPlacementSide` should be right.
 */
export const handleDetachment = (_entity, _entityPlacementSide) => {
    const attachmentSide = getOppositeSide(_entityPlacementSide);
    const component = getRemovedSideUnitComponentBasedOnSide(attachmentSide);
    const hasDetachedSideUnit = _entity.hasComponent(component);
    if (hasDetachedSideUnit)
        _entity.removeComponent(component);
    handleCrossBrace(_entity);
};
export const collectAttachmentInformation = (_entity, _entityPlacementSide) => {
    var _a;
    const attachmentSide = getOppositeSide(_entityPlacementSide);
    const oppositeSideComponent = getAttachmentComponentBySide(attachmentSide);
    const newAttachment = (_a = _entity.getComponent(oppositeSideComponent)) === null || _a === void 0 ? void 0 : _a.attachedTo;
    return { attachmentSide, newAttachment };
};
export const getSideUnit = (entity, side) => {
    const sideUnit = selectChildEntityByPartKey(entity, sideUnitKeys[side]);
    if (!sideUnit)
        throw new Error("Side unit could not be found: " + entity);
    return sideUnit;
};
export const getSideComponent = (_attachmentSide) => _attachmentSide === LEFT
    ? RemovedLeftSideUnitComponent
    : RemovedRightSideUnitComponent;
export const removeSideUnit = (_entity, side, component, condition = true) => {
    if (!condition)
        return;
    const sideUnit = getSideUnit(_entity, side);
    const productRef = getEntityProductRef(sideUnit);
    _entity.addComponent(new component(productRef));
    ConnectionUtils.disconnectAndDisposeEntityAndHierarchy(sideUnit);
};
export const handleEntitiesAreEqualHeight = (_entity, _entityPlacementSide) => {
    if (_entityPlacementSide !== LEFT)
        return;
    if (!hasRemovedSideUnit(_entity, getOppositeSide(_entityPlacementSide))) {
        removeSideUnit(_entity, getOppositeSide(_entityPlacementSide), RemovedRightSideUnitComponent);
    }
    handleCrossBrace(_entity);
};
export const handleEntitiesAreDifferentHeight = (_entity, _newAttachment, _attachmentSide) => {
    const isLowest = getShortestEntity(_entity, _newAttachment).id === _entity.id;
    const component = getSideComponent(_attachmentSide);
    if (!hasRemovedSideUnit(_entity, _attachmentSide))
        removeSideUnit(_entity, _attachmentSide, component, isLowest);
    handleCrossBrace(_entity);
};
export const handleSingleSideAttachment = (_entity, _entityPlacementSide) => {
    const { attachmentSide, newAttachment } = collectAttachmentInformation(_entity, _entityPlacementSide);
    if (!newAttachment)
        throw new Error("Something went wrong when setting attachments.");
    entitiesAreEqualHeight(_entity, newAttachment)
        ? handleEntitiesAreEqualHeight(_entity, _entityPlacementSide)
        : handleEntitiesAreDifferentHeight(_entity, newAttachment, attachmentSide);
};
